/**
 * Who builds which screen. Placeholders read this so everyone can see the plan.
 * Workers: when you ship a screen, replace its placeholder and delete its entry's `pending` flag.
 */
export type ScreenId =
  | "workspace-home"
  | "workspace-settings"
  | "new-project"
  | "canvas"
  | "deployments"
  | "deployment-detail"
  | "logs"
  | "metrics"
  | "safety"
  | "spend"
  | "agents"
  | "settings-variables"
  | "settings-domains"
  | "settings-environments"
  | "settings-members"
  | "settings-manifest"
  | "domain-wizard"
  | "domain-detail"
  | "compliance"
  | "approve"
  | "badge"
  | "login";

export interface Worker {
  id: string;
  name: string;
  area: string;
}

export const workers = {
  w1: { id: "ui-1", name: "UI worker 1", area: "Projects, canvas and first-run deploy" },
  w2: { id: "ui-2", name: "UI worker 2", area: "Deployments, logs and metrics" },
  w3: { id: "ui-3", name: "UI worker 3", area: "Safety Centre, spend and compliance" },
  w4: { id: "ui-4", name: "UI worker 4", area: "Agents, approvals, settings, domains and sign-in" },
} satisfies Record<string, Worker>;

export interface ScreenPlan {
  title: string;
  owner: Worker;
  summary: string;
  /** What the finished screen does (from docs/12). */
  features: string[];
  /** Hooks from "@/lib/api" the screen is expected to use. */
  hooks: string[];
}

export const screens: Record<ScreenId, ScreenPlan> = {
  "workspace-home": {
    title: "Projects",
    owner: workers.w1,
    summary: "Every project at a glance: is it up, is it safe, what is it costing.",
    features: ["Projects grid with health dot, spend this month and safety grade", "Pending approvals and recent agent activity", "Workspace spend against the cap", "Empty state that gets a first app deployed in under 3 minutes"],
    hooks: ["useProjects(workspace)", "useProjectStatus(project)", "useWorkspaceSpend(workspace)", "useApprovals(workspace, { status: 'pending' })", "useActivity(workspace)"],
  },
  "workspace-settings": {
    title: "Workspace settings",
    owner: workers.w4,
    summary: "Members, billing, plan and workspace-wide agent tokens.",
    features: ["Members and roles, passkey status", "Plan, payment method, invoices (VAT-correct)", "Workspace spend cap", "Webhooks"],
    hooks: ["useWorkspace(workspace)", "useMembers(workspace)", "useBilling(workspace)", "useInvoices(workspace)", "useSpendCaps(workspace)"],
  },
  "new-project": {
    title: "New project",
    owner: workers.w1,
    summary: "Connect GitHub or deploy with your AI, see the plan in plain English, fill variables, watch a friendly build log, celebrate.",
    features: ["“Connect GitHub” or “Deploy with your AI”", "Repo picker with stack detection: “We'll build a Next.js app with Node 22…”", "Detected variables with secret inputs", "Live friendly build log, raw log one click away", "Success: live URL, QR code, add domain, “Invite your AI” MCP card"],
    hooks: ["useCreateProject(workspace)", "api.GET('/v1/workspaces/{workspace}/github/installations')", "api.POST('/v1/workspaces/{workspace}/github/detect')", "useOperationStream(operationId)"],
  },
  canvas: {
    title: "Canvas",
    owner: workers.w1,
    summary: "The project as a living graph: services, databases, volumes, domains and the calls between them.",
    features: ["xyflow canvas of services, databases, volumes, cron jobs and domains", "Node cards: status, current deploy, CPU sparkline, £/day, domain chip", "Click a node for a side panel with deployments, variables, logs, metrics, settings", "Observed private-network edges; preview environments as ghost branches", "Drag to add a database; drag an edge to create a reference variable"],
    hooks: ["useProjectGraph(project, env)", "useUpdateGraphLayout(project, env)", "useServiceInstance(project, env, service)", "useDeployments(project, { service })"],
  },
  deployments: {
    title: "Deployments",
    owner: workers.w2,
    summary: "Every build, deploy and rollback: who or which agent did it, what changed, how long it took.",
    features: ["Timeline grouped by day, filter by service, environment, status and actor", "Agent attribution (“via claude-code-on-stewart-laptop”)", "Rollback and redeploy with dry-run plan", "Live progress for in-flight deploys"],
    hooks: ["useDeployments(project, filter)", "useRollbackDeployment(project)", "useOperationStream(operationId)"],
  },
  "deployment-detail": {
    title: "Deployment",
    owner: workers.w2,
    summary: "One deploy in depth, with the failure screen when it goes wrong.",
    features: ["Red banner with one-sentence cause and fix: “Your app listens on port 8080 but we expected 3000.” [Apply]", "“Ask AI to fix → opens PR”", "Friendly build log with raw log toggle", "Image, commit, scan results, duration"],
    hooks: ["useDeployment(id)", "useDeploymentExplanation(id)", "useDeploymentLogs(id)", "useBuild(buildId)", "useOperationStream(operationId)"],
  },
  logs: {
    title: "Logs",
    owner: workers.w2,
    summary: "Unified search and live tail across services, with filters by service and level.",
    features: ["Live tail over SSE with pause/resume", "Search, level and service filters, time range", "HTTP lines with status and latency", "Customer content rendered as untrusted text"],
    hooks: ["useLogs(project, filter)", "useLogTail(project, filter)"],
  },
  metrics: {
    title: "Metrics",
    owner: workers.w2,
    summary: "CPU, memory, requests, latency and errors per service, with a cost overlay.",
    features: ["Per-service charts with limit lines", "Cost overlay in £/hour", "Time range selector", "Explain panels for each metric"],
    hooks: ["useMetrics(project, { metric: ['cpu','memory',…], environment, from, to })"],
  },
  safety: {
    title: "Safety Centre",
    owner: workers.w3,
    summary: "A plain-English security grade from A to E with a checklist; every item has a one-click fix or an AI fix PR.",
    features: ["Grade and score with what moved it", "Checklist by category, failures first; blocking items flagged", "One-click fix, AI fix PR, acknowledge with reason", "Shareable “Secured on Guv” badge"],
    hooks: ["useSafetyReport(project, env)", "useApplySafetyFix(project)", "useAcknowledgeSafetyCheck(project)", "useRunSafetyScan(project)"],
  },
  spend: {
    title: "Spend",
    owner: workers.w3,
    summary: "“This month: £7.42 of your £10 cap”, with a forecast line and per-service “why” annotations.",
    features: ["Month-to-date vs cap with forecast", "Daily spend chart, forecast dashed", "Per-service breakdown with why: “api used 0.3 vCPU on average”", "Cap controls front and centre (raising needs typed confirmation)"],
    hooks: ["useProjectSpend(project)", "useSpendCaps(workspace)", "useUpdateSpendCap(workspace)", "useRateCard()"],
  },
  agents: {
    title: "Agents",
    owner: workers.w4,
    summary: "Agent tokens with scopes as sentences, a pending approvals inbox and an activity feed.",
    features: ["Tokens: “Can deploy to staging and previews. Cannot touch production.”", "Create token wizard with budget and expiry; MCP connect command", "Pending approvals inbox", "Agent activity feed and session replay"],
    hooks: ["useAgentTokens(workspace)", "useCreateAgentToken(workspace)", "useRevokeAgentToken(workspace)", "useApprovals(workspace, { project })", "useActivity(workspace, { project, actor_type: 'agent' })"],
  },
  "settings-variables": {
    title: "Variables & secrets",
    owner: workers.w4,
    summary: "Environment variables and write-only secrets.",
    features: ["Variables with references like ${{ main.url }}", "Secrets are write-only: set, rotate, never shown", "Redeploy prompt after changes", "Show as code"],
    hooks: ["useVariables(project, env)", "useSetVariables(project, env)", "useSecrets(project, env)", "useSetSecret(project, env)"],
  },
  "settings-domains": {
    title: "Domains",
    owner: workers.w4,
    summary: "Generated and custom domains, certificate state, dangling DNS warnings.",
    features: ["Domain list with live status", "Add domain launches the wizard", "Orphaned domain fix-ups"],
    hooks: ["useDomains(project, env)", "useVerifyDomain(project, env)"],
  },
  "settings-environments": {
    title: "Environments",
    owner: workers.w4,
    summary: "Production, staging and previews; protection rules; TTLs; database branching mode.",
    features: ["Protection rules and approvers", "Preview environments with TTL and cost cap", "Database mode: new, branch, branch-masked"],
    hooks: ["useEnvironments(project)"],
  },
  "settings-members": {
    title: "Members",
    owner: workers.w4,
    summary: "Who can do what in this project.",
    features: ["Roles: Owner, Admin, Developer, Viewer, Billing", "Passkey status per member", "Invite by email"],
    hooks: ["useMembers(workspace)"],
  },
  "settings-manifest": {
    title: "Manifest",
    owner: workers.w4,
    summary: "guv.yaml status: managed by manifest, last applied, drift.",
    features: ["Current guv.yaml export", "Last applied commit and plan hash", "Drift detection with fix"],
    hooks: ["useManifest(project)", "useManifestStatus(project)"],
  },
  "domain-wizard": {
    title: "Add a domain",
    owner: workers.w4,
    summary: "Detects your registrar and shows tailored steps for 123-reg, GoDaddy, Namecheap, Cloudflare, IONOS and Fasthosts.",
    features: ["Registrar detection (RDAP)", "Registrar-specific instructions with screenshots", "Live polling with animated checks: DNS → ownership → certificate → live"],
    hooks: ["useAddDomain(project, env)", "useDomain(project, env, host, { refetchInterval: 3000 })", "useVerifyDomain(project, env)"],
  },
  "domain-detail": {
    title: "Domain",
    owner: workers.w4,
    summary: "One domain's DNS records, checks and certificate.",
    features: ["Records with copy buttons and observed values", "Check timeline", "Certificate expiry"],
    hooks: ["useDomain(project, env, host)"],
  },
  compliance: {
    title: "Compliance",
    owner: workers.w3,
    summary: "Data location, DPA, sub-processors and audit log export (Business plan).",
    features: ["Data location statement (UK)", "DPA and sub-processor list", "NCSC 14 principles and DSPT evidence pack", "Hash-chained audit log export"],
    hooks: ["useProject(project)", "useAuditEvents(workspace)"],
  },
  approve: {
    title: "Approve a request",
    owner: workers.w4,
    summary: "The page an approval link opens, usually on a phone: what the agent wants, why it needs you, the plan, approve with a passkey.",
    features: ["Who is asking and why", "Plain-English plan with cost change", "Approve with passkey (step-up) or deny with a note", "Result once executed"],
    hooks: ["useApproval(id)", "useApproveApproval()", "useDenyApproval()"],
  },
  badge: {
    title: "Secured on Guv",
    owner: workers.w3,
    summary: "Public summary behind the “Secured on Guv: Grade A” badge.",
    features: ["Grade, checked date, data location", "Category pass/warn list without sensitive detail", "Growth-loop CTA"],
    hooks: ["usePublicSafetySummary(badgeId)"],
  },
  login: {
    title: "Sign in",
    owner: workers.w4,
    summary: "Passkey-first sign in, with GitHub as secondary. No SMS.",
    features: ["Passkey sign in and registration", "GitHub OAuth", "Email verification for new accounts", "Dev login when GUV_DEV_LOGIN=true"],
    hooks: ["api.POST('/v1/auth/passkeys/login/begin')", "api.POST('/v1/auth/dev-login')"],
  },
};
